import {ListItem} from '@mui/material';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useActionsHandler} from '@ELib/components/EList/EList';
import {useContextMenu} from '@ELib/functions/contextMenu';
import {classy} from '@ELib/utilities/HelperFunctions';

import {StyledList} from './styles';
import {
  EListActiveChildItem,
  EListActiveEntry,
  EListEntryActionHandler,
  EListEntryClickHandler,
  EListEntryDragHandler,
  EListEntryDropHandler,
  EListItemEntry,
} from './types';
import {isActiveItem} from './utilities';

export const EListEntry = <T extends object>({
  entry,
  onEntryClick,
  actions,
  parentEntry,
  activeItem,
  onToggleExpansion,
  isExpanded,
  isDraggable,
  shouldActivateOnContextClick = true,
  onEntryDragStart,
  onEntryDragEnd,
  onDropAccept,
  isSelected,
}: {
  entry: EListItemEntry<T>;
  parentEntry?: EListItemEntry<T>;
  subRowIndex?: number;
  isSelected?: boolean;
  activeItem: EListActiveEntry | undefined;
  isExpanded?: boolean;
  isDraggable?: boolean;
  shouldActivateOnContextClick?: boolean;
  onEntryClick: EListEntryClickHandler<T>;
  actions?: EListEntryActionHandler<T>;
  onToggleExpansion?: (entry: EListItemEntry<T>) => void;
  onEntryDragStart?: EListEntryDragHandler<T>;
  onEntryDragEnd?: EListEntryDragHandler<T>;
  onDropAccept?: EListEntryDropHandler<T>;
}): React.ReactElement => {
  const entryActions = useMemo(() => actions?.(entry) || [], [actions, entry]);
  const isActivatable =
    entry.options?.isActivatable === undefined || entry.options.isActivatable;

  const onEntryDoubleClick = useCallback(() => {
    const inactive = entryActions[0]?.inactive;
    if (!inactive) {
      entryActions[0]?.callback?.(entry.raw);
    }
  }, [entry.raw, entryActions]);

  const {contextMenuCallbacks, contextMenu} = useContextMenu(
    entryActions,
    entry.raw
  );

  const onClick = useCallback(
    (event: React.MouseEvent) => {
      if (!isActivatable) {
        return;
      }
      if (parentEntry) {
        onEntryClick(event, parentEntry, entry);
      } else {
        onEntryClick(event, entry);
      }
    },
    [isActivatable, onEntryClick, parentEntry, entry]
  );

  const openContextMenu = useCallback(
    (event: React.MouseEvent) => {
      if (entryActions.length) {
        if (
          !isActiveItem(entry.id, activeItem) &&
          shouldActivateOnContextClick
        ) {
          onClick(event);
        }
        contextMenuCallbacks.onContextMenu(event);
      } else {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [
      activeItem,
      contextMenuCallbacks,
      entry.id,
      entryActions,
      onClick,
      shouldActivateOnContextClick,
    ]
  );

  const toggleEntry = useCallback(
    () => onToggleExpansion?.(entry),
    [entry, onToggleExpansion]
  );

  const onDragStart = useCallback(
    (dragEvent: React.DragEvent<HTMLTableRowElement>) => {
      onEntryDragStart?.({dragEvent, activeEntry: entry});
    },
    [entry, onEntryDragStart]
  );

  const onDragEnd = useCallback(
    (dragEvent: React.DragEvent<HTMLTableRowElement>) =>
      onEntryDragEnd?.({dragEvent}),
    [onEntryDragEnd]
  );

  return (
    <li
      key={entry.id}
      className={classy({
        noHover: !isActivatable,
        selected: Boolean(isSelected),
        active:
          typeof activeItem === 'string'
            ? activeItem === entry.id
            : activeItem?.childId === entry.id,
        expanded: Boolean(isExpanded) && isActiveItem(entry.id, activeItem),
        disabled: Boolean(entry.options?.disabled),
        sticky: Boolean(entry.id === entry.stickyHeaderId),
      })}
      data-id={entry.id}
    >
      <>
        <div
          draggable={isDraggable}
          onDragEnd={onDragEnd}
          onDragStart={onDragStart}
        >
          <entry.Component
            isExpanded={Boolean(isExpanded)}
            item={entry.raw}
            onClick={onClick}
            onContextMenu={openContextMenu}
            onDoubleClick={onEntryDoubleClick}
            onDropAccept={onDropAccept}
            onToggleExpansion={toggleEntry}
          />
          {contextMenu}
        </div>
        {entry.options?.expansion && isExpanded && (
          <EListSubEntry
            activeItem={activeItem}
            parentEntry={entry}
            onEntryClick={onEntryClick}
          />
        )}
      </>
    </li>
  );
};

const EListSubEntry = <T extends object>({
  parentEntry,
  activeItem,
  onEntryClick,
}: {
  parentEntry: EListItemEntry<T>;
  activeItem: EListActiveEntry | undefined;
  onEntryClick: EListEntryClickHandler<T>;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [subItems, setSubItems] = useState<EListItemEntry<T>[]>();
  const subItemsExist = Boolean(subItems);

  /** Ref used to avoid unnecessary re-renderings of subrows. */
  const parentEntryRef = useRef<EListItemEntry<T>>();
  parentEntryRef.current = parentEntry;

  // load potentially async sub entry or sub entries
  useEffect(() => {
    const getSubItems = async () => {
      if (!parentEntryRef.current?.options?.expansion) {
        return;
      }

      if (!subItemsExist) {
        setIsLoading(true);
      }
      const subRowsResult = await parentEntryRef.current.options.expansion();
      setIsLoading(false);
      setSubItems(subRowsResult);
    };
    getSubItems();
  }, [
    parentEntryRef,
    subItemsExist,
    parentEntryRef.current.options?.expansion,
  ]);

  // holds the actions callable on an entry
  const actionsHandler: EListEntryActionHandler<T> = useActionsHandler(
    subItems || [],
    new Set(activeItem ? (activeItem as EListActiveChildItem).childId : [])
  );

  if (isLoading) {
    return (
      <StyledList>
        <ListItem>Loading ...</ListItem>
      </StyledList>
    );
  }

  if (!subItems) {
    // avoid flashing of empty content while the useEffect is still running
    return null;
  }

  return (
    <StyledList>
      {subItems.map((subItem) => (
        <EListEntry
          key={subItem.id}
          actions={actionsHandler}
          activeItem={activeItem}
          entry={subItem}
          parentEntry={parentEntry}
          onEntryClick={onEntryClick}
        />
      ))}
    </StyledList>
  );
};
